import { FC, useState } from 'react';

import { ImagesApp } from '../../../constants/images';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import formatNumericValue from '../../../utils/formatNumericValue';
import * as Styles from './summary.styles';

type FeesBreakdownProps = {
  serviceFee: number;
  currencyFormat: string;
};

/**
 * Discloses the fees on top of the tickets before the buyer leaves the cart
 * (Decreto 13.108/2026, art. 7º). Only the service fee is known here: the
 * processing fee and installment interest depend on the payment method, so the
 * gateway prices them on the payment step and this block only announces them.
 */
const FeesBreakdown: FC<FeesBreakdownProps> = ({ serviceFee, currencyFormat }) => {
  const { translate } = useI18nContext();
  // Open by default: the fees must be shown itemised, not behind a click.
  const [showDetails, setShowDetails] = useState(true);
  /*
   * The explanation opens in place on click rather than in a hover tooltip, so it reaches
   * touch, keyboard and screen-reader users too — the summary also renders on mobile, where
   * the hover-only Popover never shows.
   */
  const [showInfo, setShowInfo] = useState(false);

  const formattedServiceFee = formatNumericValue({ value : serviceFee, discount : 0, currencyFormat });

  return (
    <Styles.Fees id="summary-fees">
      <Styles.FeesRow>
        <Styles.FeesTitle id="summary-fees-title">
          {translate(['SUMMARY_FEES', 'TITLE'])}
          <Styles.FeesInfoTrigger
            type="button"
            id="summary-fees-info"
            aria-label={translate(['SUMMARY_FEES', 'INFO_LABEL'])}
            aria-expanded={showInfo}
            aria-controls="summary-fees-info-text"
            onClick={() => setShowInfo((current) => !current)}
          >
            <img src={ImagesApp.svg.icInfoPayment} alt="" />
          </Styles.FeesInfoTrigger>
        </Styles.FeesTitle>
        <Styles.FeesValue id="summary-fees-total">{formattedServiceFee}</Styles.FeesValue>
      </Styles.FeesRow>
      <Styles.FeesNote id="summary-fees-info-text" hidden={!showInfo}>
        {translate(['SUMMARY_FEES', 'INFO'])}
      </Styles.FeesNote>
      <Styles.FeesToggle
        type="button"
        id="summary-fees-toggle"
        aria-expanded={showDetails}
        aria-controls="summary-fees-details"
        onClick={() => setShowDetails((current) => !current)}
      >
        {translate(['SUMMARY_FEES', showDetails ? 'HIDE_DETAILS' : 'SHOW_DETAILS'])}
        <Styles.FeesChevron src={ImagesApp.svg.arrowUp} alt="" $collapsed={!showDetails} />
      </Styles.FeesToggle>
      {/* Kept mounted so `aria-controls` above always points at an element. */}
      <Styles.FeesDetails id="summary-fees-details" hidden={!showDetails}>
        <Styles.FeesRow>
          <Styles.FeesValue>{translate(['SUMMARY_FEES', 'SERVICE_FEE'])}</Styles.FeesValue>
          <Styles.FeesValue id="summary-fees-service-fee">{formattedServiceFee}</Styles.FeesValue>
        </Styles.FeesRow>
        <Styles.FeesNote id="summary-fees-payment-method-note">
          {translate(['SUMMARY_FEES', 'PAYMENT_METHOD_FEES'])}
        </Styles.FeesNote>
      </Styles.FeesDetails>
      <Styles.Divider />
    </Styles.Fees>
  );
};

export default FeesBreakdown;
